import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { markStripeReturnHandled } from "../lib/stripeFlow";
import { isSafeInternalPath } from "../utils/safeInternalPath";
import { inAppPath, isTalentPagePath } from "../lib/appLinks";

// The app side of the Stripe return trip (and of any
// requesta.app link a talent taps with the app installed).
//
// Two inbound shapes, one handler:
//   1. Universal links: https://(www.)requesta.app/<path> — iOS hands the URL
//      to the app instead of Safari (associated-domains entitlement + the AASA
//      file in public/.well-known/). Android 12+ does the same for talent page
//      links (the AppLinks alias in AndroidManifest.xml + assetlinks.json).
//   2. Custom scheme: requestapp://<path> — the "Return to Request App" button
//      on the web callback page when the universal link didn't auto-fire.
//
// Both are delivered by @capacitor/app as appUrlOpen (warm app) or as the
// launch URL (cold start). We close any in-app browser sheet that's still up
// (the Stripe onboarding sheet) and route the SPA to the same path the web
// would have shown.
const APP_HOSTS = new Set(["requesta.app", "www.requesta.app"]);
const CUSTOM_SCHEME = "requestapp:";

const toInternalPath = (raw: string): string | null => {
  const path = parseInternalPath(raw);
  // A link such as https://requesta.app//evil.com parses to a path the
  // browser reads as another website. Drop it.
  return isSafeInternalPath(path) ? path : null;
};

const parseInternalPath = (raw: string): string | null => {
  try {
    if (raw.startsWith(CUSTOM_SCHEME)) {
      // requestapp://register/stripe/callback?nonce=X → /register/stripe/callback?nonce=X
      const rest = raw.slice(CUSTOM_SCHEME.length).replace(/^\/+/, "");
      return rest ? `/${rest}` : "/";
    }
    const url = new URL(raw);
    if ((url.protocol === "https:" || url.protocol === "http:") && APP_HOSTS.has(url.hostname)) {
      return `${url.pathname}${url.search}${url.hash}` || "/";
    }
  } catch {
    /* unparseable — ignore */
  }
  return null;
};

// getLaunchUrl() keeps answering the same link for the life of the process —
// Android: the intent that cold-started the app; iOS: the LAST link that opened
// it, warm or cold. Record each link we follow so that answer is acted on once:
// not again on a remount, and not again after the in-place reload the Stripe
// return does. sessionStorage survives that reload and is gone when the app
// process ends, which is also when iOS forgets the link (Android: below).
const HANDLED_KEY = "rq_deeplink_handled";

const alreadyFollowed = (raw: string): boolean => {
  try {
    return sessionStorage.getItem(HANDLED_KEY) === raw;
  } catch {
    return false;
  }
};

const recordFollowed = (raw: string) => {
  try {
    sessionStorage.setItem(HANDLED_KEY, raw);
  } catch {
    /* storage blocked — the effect below still runs once per mount */
  }
};

// Android needs more (2026-10-10). Its launch link is the link that started
// the app PROCESS, so remembering only the last link followed replayed it on
// the next full reload (a push tap, the post-payment reload) once any other
// link had been followed since. And when Android restores an app it killed,
// it hands that link over again — to getLaunchUrl() AND appUrlOpen — with
// sessionStorage empty. So Android keeps the launch links it followed on the
// device: each is followed once. Its requestapp:// links (the returns from
// Stripe and Instagram) are one-time, so a repeat is almost always a replay.
const LAUNCHES_KEY = "rq_deeplink_launches";
const MAX_LAUNCHES = 20;

const followedLaunches = (): string[] => {
  try {
    const list = JSON.parse(localStorage.getItem(LAUNCHES_KEY) || "[]");
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

const rememberLaunch = (raw: string) => {
  try {
    const rest = followedLaunches().filter((u) => u !== raw);
    localStorage.setItem(LAUNCHES_KEY, JSON.stringify([...rest, raw].slice(-MAX_LAUNCHES)));
  } catch {
    /* storage blocked — sessionStorage above still covers in-app reloads */
  }
};

// A talent's page link is the exception (1.5.2): a fan taps the same one again
// at the next gig, so once ever would open the app on its home instead. It
// counts as a replay only within a few hours of following it — the window in
// which Android restores the app it killed.
const TALENT_LAUNCH_KEY = "rq_deeplink_talent_launch";
const TALENT_REPLAY_MS = 6 * 60 * 60 * 1000;

const isTalentLink = (raw: string): boolean => {
  const path = toInternalPath(raw);
  return !!path && isTalentPagePath(path);
};

const recentTalentLaunch = (raw: string): boolean => {
  try {
    const last = JSON.parse(localStorage.getItem(TALENT_LAUNCH_KEY) || "null");
    return last?.url === raw && Date.now() - last.at < TALENT_REPLAY_MS;
  } catch {
    return false;
  }
};

const rememberTalentLaunch = (raw: string) => {
  try {
    localStorage.setItem(TALENT_LAUNCH_KEY, JSON.stringify({ url: raw, at: Date.now() }));
  } catch {
    /* storage blocked — the link is followed, a restore may replay it */
  }
};

// Android re-delivers the launch link to appUrlOpen as the app starts; the same
// link arriving after this is the fan tapping it again.
const STARTUP_REDELIVERY_MS = 5000;

const DeepLinkHandler: React.FC = () => {
  const navigate = useNavigate();
  // useNavigate() is a new function on every page change under BrowserRouter.
  // Read it through a ref so the effect below runs once per mount: re-running
  // it re-read the launch link and dragged the user back to that page.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const android = Capacitor.getPlatform() === "android";

    // A cold-start URL can arrive via BOTH getLaunchUrl and appUrlOpen —
    // remember what we just handled so it only routes once.
    let lastHandled = "";
    let lastHandledAt = 0;

    const handle = (raw: string | undefined | null) => {
      if (!raw) return;
      const now = Date.now();
      if (raw === lastHandled && now - lastHandledAt < 3000) return;
      lastHandled = raw;
      lastHandledAt = now;

      const path = toInternalPath(raw);
      if (!path) return;
      // Before the in-place reload below, so the reloaded app doesn't replay it.
      recordFollowed(raw);

      // Dismiss the in-app Stripe sheet if it's still presented — the deep
      // link IS the "I'm done in there" signal. Safe no-op otherwise. Mark
      // Stripe returns FIRST: closing the sheet fires browserFinished, and
      // the sheet-close self-completion must not double-navigate.
      if (path.startsWith("/register/stripe/callback")) markStripeReturnHandled();
      Browser.close().catch(() => {});

      if (path.startsWith("/profile") && path.includes("stripeReturn=")) {
        // The post-Stripe return carries a one-time session token that ONLY
        // AuthContext's boot sequence knows how to exchange — do a full
        // in-place reload (local bundle, fast) so the exact web code path
        // runs: token exchange, fresh user, /profile.
        window.location.href = path;
        return;
      }
      navigateRef.current(inAppPath(path), { replace: true });
    };

    // The launch link is only followed if this app session hasn't already
    // (Android: if this device never has).
    const launch = CapApp.getLaunchUrl()
      .then((res) => res?.url || null)
      .catch(() => null);
    launch.then((url) => {
      if (!url || alreadyFollowed(url)) return;
      if (android && isTalentLink(url)) {
        if (recentTalentLaunch(url)) return;
        rememberTalentLaunch(url);
      } else if (android) {
        if (followedLaunches().includes(url)) return;
        rememberLaunch(url);
      }
      handle(url);
    });

    // A link tapped while the app is open is always a fresh request — even
    // the same link again — so it is followed unconditionally. On Android,
    // every app start also delivers the launch link here: that one is the
    // launch branch's call above — unless it is a talent's page tapped again
    // after the start.
    const mountedAt = Date.now();
    const subPromise = CapApp.addListener("appUrlOpen", (event: { url: string }) => {
      if (!android) {
        handle(event.url);
        return;
      }
      const tappedAgain = isTalentLink(event.url) && Date.now() - mountedAt > STARTUP_REDELIVERY_MS;
      launch.then((url) => {
        if (event.url !== url || tappedAgain) handle(event.url);
      });
    });

    return () => {
      subPromise.then((s) => s.remove()).catch(() => {});
    };
  }, []);

  return null;
};

export default DeepLinkHandler;
